import type { BimWorkOrderChangeOtherPriceData, SectionEnum } from 'gql/graphql';
import { BimContractOtherStatusEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { OtherViewModal } from 'pages/Project/Common/Contract/OtherView';
import { Button, ContextMenu, IconKebab } from 'uikit';

import * as R from 'ramda';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BimWorkOrderChangeEditDistribution } from '../../BimWorkOrderChangeEditDistribution';
import { BimWorkOrderChangeEditOther } from '../../BimWorkOrderChangeEditOther';
import { useContractChangeConstructionTypes } from '../../hooks';

export type TGroupedTableItem = BimWorkOrderChangeOtherPriceData & {
    id: SectionEnum | string;
    name: string;
    isGroupTitle: boolean;
};

type TWorkOrderChangeOtherPriceMenuProps = {
    data: TGroupedTableItem | undefined;
    openSingleDeleteHandler: () => void;
    openExcludeHandler: () => void;
    refreshTable: () => void;
    isDeletionDisabled: boolean;
    constructionTypeId: number | undefined;
    disabledByWorkOrderChangeStatus: boolean;
};

enum ModalType {
    ViewModal = 'ViewModal',
    EditModal = 'EditModal',
    DistributionModal = 'DistributionModal',
}

const menuGroups = [{ id: 1 }, { id: 2 }];

export const WorkOrderChangeOtherPriceMenu = ({
    data,
    openSingleDeleteHandler,
    openExcludeHandler,
    isDeletionDisabled,
    constructionTypeId,
    refreshTable,
    disabledByWorkOrderChangeStatus,
}: TWorkOrderChangeOtherPriceMenuProps) => {
    const { orderChangeId } = useParams();

    const workOrderChangeId = Number(orderChangeId);

    const buttonRef = useRef(null);
    const [isContextOpen, setIsContextOpen] = useState<boolean>(false);
    const [openedModal, setOpenedModal] = useState<ModalType | null>(null);

    const openModalHandler = useCallback((type: ModalType) => {
        setOpenedModal(type);
    }, []);
    const closeModalHandler = useCallback(() => {
        setOpenedModal(null);
    }, []);

    const isEditDisabled = useMemo(
        () =>
            disabledByWorkOrderChangeStatus ||
            !(data?.isAddedRow && data?.status === BimContractOtherStatusEnum.NewRecord),
        [data?.isAddedRow, data?.status, disabledByWorkOrderChangeStatus],
    );
    const canExcludeRow = useMemo(() => Boolean(data?.canExcludeRow), [data?.canExcludeRow]);
    const isExcludedRow = useMemo(() => Boolean(data?.isExcludedRow), [data?.isExcludedRow]);

    const menuItems = useMemo(
        () => [
            ...(isEditDisabled
                ? [
                      {
                          groupId: 1,
                          label: 'Просмотреть',
                          action: () => {
                              openModalHandler(ModalType.ViewModal);
                          },
                      },
                  ]
                : [
                      {
                          groupId: 1,
                          label: 'Редактировать',
                          action: () => {
                              openModalHandler(ModalType.EditModal);
                          },
                          disabled: isEditDisabled,
                      },
                  ]),
            ...(disabledByWorkOrderChangeStatus
                ? []
                : [
                      {
                          groupId: 1,
                          label: 'Изменить распределение',
                          action: () => {
                              openModalHandler(ModalType.DistributionModal);
                          },
                      },
                  ]),
            ...(disabledByWorkOrderChangeStatus || !canExcludeRow
                ? []
                : [
                      {
                          groupId: 1,
                          label: 'Исключить',
                          action: () => {
                              openExcludeHandler();
                          },
                      },
                  ]),
            ...(isDeletionDisabled
                ? []
                : [
                      {
                          groupId: 2,
                          label: 'Удалить',
                          action: () => {
                              openSingleDeleteHandler();
                          },
                          disabled: isDeletionDisabled,
                      },
                  ]),
        ],
        [
            canExcludeRow,
            disabledByWorkOrderChangeStatus,
            isDeletionDisabled,
            isEditDisabled,
            openExcludeHandler,
            openModalHandler,
            openSingleDeleteHandler,
        ],
    );
    const handlerItemClick = useCallback(({ action }) => action, []);

    const onContextMenuButtonClick = useCallback((evt) => {
        evt.stopPropagation();
        setIsContextOpen(R.not);
    }, []);

    const constructionTypesWithEffect = useMemo(
        () =>
            Boolean(workOrderChangeId && (openedModal === ModalType.EditModal || openedModal === ModalType.ViewModal)),
        [openedModal, workOrderChangeId],
    );

    const [constructionTypes] = useContractChangeConstructionTypes(workOrderChangeId, constructionTypesWithEffect);

    return (
        <>
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                ref={buttonRef}
                onClick={onContextMenuButtonClick}
                disabled={isExcludedRow}
            />
            <ContextMenu
                isOpen={isContextOpen}
                items={menuItems}
                groups={menuGroups}
                anchorRef={buttonRef}
                direction="downStartLeft"
                onClickOutside={onContextMenuButtonClick}
                getItemOnClick={handlerItemClick}
                size="m"
            />
            {openedModal === ModalType.EditModal && (
                <BimWorkOrderChangeEditOther
                    refreshTable={refreshTable}
                    workOrderChangeOtherPriceId={data?.workOrderChangeOtherPriceId}
                    workOrderOtherPriceId={data?.workOrderOtherPriceId}
                    closeModalHandler={closeModalHandler}
                    constructionTypeId={constructionTypeId}
                    constructionTypes={constructionTypes}
                    isOpened
                />
            )}
            {openedModal === ModalType.ViewModal && (
                <OtherViewModal
                    methodology={ProjectMethodologyEnum.Bim}
                    onClose={closeModalHandler}
                    constructionTypes={constructionTypes}
                    workOrderChangeOtherId={data?.workOrderChangeOtherPriceId}
                    workOrderOtherId={data?.workOrderOtherPriceId}
                    costRounding={0}
                />
            )}
            {openedModal === ModalType.DistributionModal && (
                <BimWorkOrderChangeEditDistribution
                    dataRow={data}
                    onCloseModal={closeModalHandler}
                    constructionTypeId={constructionTypeId}
                    refreshTable={refreshTable}
                />
            )}
        </>
    );
};

WorkOrderChangeOtherPriceMenu.displayName = 'WorkOrderChangeOtherPriceMenu';
